/**
 * DashboardErrorBanners Component
 *
 * Displays error, warning, and informational banners at the top of the dashboard.
 * Handles loader errors, operation failures, loading states, and test data indicators.
 */

import { Banner, Text, InlineStack, Spinner } from '@shopify/polaris';

export interface TestDataStatus {
  hasTestData: boolean;
  counts?: {
    trackedProducts?: number;
    alerts?: number;
  };
}

export interface DashboardErrorBannersProps {
  /** Error from loader (critical failure) */
  error?: string;
  /** Error from operations (dismissible) */
  errorBanner: string | null;
  /** Callback to dismiss operation error */
  onDismissErrorBanner: () => void;
  /** Whether products are being loaded */
  isLoadingProducts: boolean;
  /** Test data status and counts */
  testDataStatus?: TestDataStatus | null;
  /** Callback to open test data removal modal */
  onRemoveTestData?: () => void;
}

/**
 * Error and status banners for the dashboard
 */
export function DashboardErrorBanners({
  error,
  errorBanner,
  onDismissErrorBanner,
  isLoadingProducts,
  testDataStatus,
  onRemoveTestData,
}: DashboardErrorBannersProps) {
  return (
    <>
      {/* Loader error banner */}
      {error && (
        <div className="banner-enter">
          <Banner title="Error loading alerts" tone="critical" onDismiss={() => {}}>
            <Text as="p" variant="bodyMd">{error}</Text>
          </Banner>
        </div>
      )}

      {/* Operation error banner (dismissible) */}
      {errorBanner && (
        <div className="banner-enter">
          <Banner title="Operation failed" tone="critical" onDismiss={onDismissErrorBanner}>
            <Text as="p" variant="bodyMd">{errorBanner}</Text>
          </Banner>
        </div>
      )}

      {/* Loading products indicator */}
      {isLoadingProducts && (
        <div className="banner-enter">
          <Banner tone="info">
            <InlineStack gap="200" blockAlign="center">
              <Spinner size="small" accessibilityLabel="Loading products" />
              <Text as="span" variant="bodyMd">Loading products...</Text>
            </InlineStack>
          </Banner>
        </div>
      )}

      {/* Test data indicator banner */}
      {testDataStatus?.hasTestData && onRemoveTestData && (
        <div className="banner-enter">
          <Banner
            title="Test data is active"
            tone="info"
            action={{
              content: 'Remove test data',
              onAction: onRemoveTestData,
            }}
          >
            <Text as="p" variant="bodyMd">
              Your dashboard contains {testDataStatus.counts?.trackedProducts || 0} test products
              with {testDataStatus.counts?.alerts || 0} alerts. Remove test data before going live.
            </Text>
          </Banner>
        </div>
      )}
    </>
  );
}
